import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

export default function UserManagementPage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', id_perfil: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Búsqueda y paginación
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimer = useRef(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);

  const headers = useMemo(() => ({ Authorization: token ? `Bearer ${token}` : '' }), [token]);

  const loadPerfiles = useCallback(() => {
    if (!token) return Promise.resolve();
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    return axios.get(`${API_BASE}/admin/profiles`, { headers })
      .then(r => setPerfiles(r.data))
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.loadingProfiles')));
  }, [headers, token]);

  const loadUsers = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const params = { page, size };
    if (debouncedSearch) params.search = debouncedSearch;
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.get(`${API_BASE}/admin/users/query`, { headers, params })
      .then(r => {
        setUsers(r.data.items);
        setTotal(r.data.total);
        const maxPage = Math.max(1, Math.ceil(r.data.total / size));
        if (page > maxPage) {
          setPage(1);
        }
      })
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.loadingUsers')))
      .finally(() => setLoading(false));
  }, [headers, token, page, size, debouncedSearch]);

  // Inicial: perfiles y usuarios
  useEffect(() => { loadPerfiles(); }, [loadPerfiles]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Debounce búsqueda
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1); // reset page when search changes
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const submitCreate = (e) => {
    e.preventDefault();
    setError(null);
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.post(`${API_BASE}/admin/users`, { ...form, id_perfil: Number(form.id_perfil) }, { headers })
      .then(() => { setForm({ email: '', password: '', id_perfil: '' }); setCreating(false); loadUsers(); })
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.creatingUser')));
  };

  const updateUser = (id, patch) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.patch(`${API_BASE}/admin/users/${id}`, patch, { headers })
      .then(() => loadUsers())
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.updatingUser')));
  };

  const totalPages = Math.max(1, Math.ceil(total / size));

  // --- Reset Password Modal State ---
  const [resetTarget, setResetTarget] = useState(null); // {id,email}
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {new_password}
  const [customPassword, setCustomPassword] = useState('');

  const openReset = (user) => {
    setResetTarget(user);
    setResetResult(null);
    setCustomPassword('');
  };
  const closeReset = () => {
    if (resetLoading) return;
    setResetTarget(null);
    setResetResult(null);
    setCustomPassword('');
  };
  const doReset = (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResetLoading(true);
    setError(null);
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.post(`${API_BASE}/admin/users/${resetTarget.id}/reset-password`, customPassword.trim() ? { new_password: customPassword.trim() } : {}, { headers })
      .then(r => {
        setResetResult(r.data);
      })
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.resettingPassword')))
      .finally(()=> setResetLoading(false));
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
  };

  // --- Desactivar / Reactivar (confirmación inline) ---
  const [confirmRow, setConfirmRow] = useState(null); // id usuario en confirmación
  const [actionLoading, setActionLoading] = useState(false);

  const requestDeactivate = (user) => {
    setConfirmRow(user.id);
  };
  const cancelDeactivate = () => setConfirmRow(null);
  const doDeactivate = (user) => {
    setActionLoading(true);
    setError(null);
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.post(`${API_BASE}/admin/users/${user.id}/deactivate`, {}, { headers })
      .then(()=> { setConfirmRow(null); loadUsers(); })
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.deactivatingUser', { defaultValue: 'Error desactivando usuario' })))
      .finally(()=> setActionLoading(false));
  };
  const doReactivate = (user) => {
    setActionLoading(true);
    setError(null);
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    axios.patch(`${API_BASE}/admin/users/${user.id}`, { estado: 'A' }, { headers })
      .then(()=> loadUsers())
      .catch(err => setError(err.response?.data?.detail || t('userManagement.errors.reactivatingUser', { defaultValue: 'Error reactivando usuario' })))
      .finally(()=> setActionLoading(false));
  };

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-4">
        <h2 className="h5 mb-0">{t('userManagement.title')}</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => loadUsers()} disabled={loading}>{t('common.refresh')}</button>
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(v => !v)}>
            {creating ? t('userManagement.cancel') : t('userManagement.createUser')}
          </button>
        </div>
      </div>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {creating && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="mb-3">{t('userManagement.modal.createTitle')}</h6>
            <form onSubmit={submitCreate} className="row g-3">
              <div className="col-md-4">
                <label className="form-label">{t('userManagement.fields.email')}</label>
                <input className="form-control" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">{t('userManagement.fields.password')}</label>
                <input className="form-control" type="text" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">{t('userManagement.fields.profile')}</label>
                <select className="form-select" value={form.id_perfil} onChange={e=>setForm(f=>({...f,id_perfil:e.target.value}))} required>
                  <option value="">{t('common.select', { defaultValue: 'Seleccione...' })}</option>
                  {perfiles.map(p=> <option key={p.id} value={p.id}>{p.descripcion || t('accessManagement.profile', { id: p.id })}</option>)}
                </select>
              </div>
              <div className="col-md-1 d-flex align-items-end">
                <button className="btn btn-success w-100" type="submit" disabled={loading}>{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
            <div className="flex-grow-1">
              <label className="form-label small mb-1">{t('userManagement.search')}</label>
              <input className="form-control form-control-sm" placeholder={t('userManagement.search')} value={search} onChange={e=>setSearch(e.target.value)} />
            </div>
            <div>
              <label className="form-label small mb-1">{t('userManagement.pagination.pageSize')}</label>
              <select className="form-select form-select-sm" value={size} onChange={e=>{ setPage(1); setSize(Number(e.target.value)); }}>
                {[5,10,20,50,100].map(n=> <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="small text-muted ms-auto">
              Total: {total} &nbsp;|&nbsp; {t('userManagement.pagination.page', { current: page, total: totalPages })}
            </div>
          </div>
          {loading ? <div className="text-muted small">{t('common.loading')}</div> : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th style={{width:'60px'}}>{t('userManagement.fields.id')}</th>
                    <th>{t('userManagement.fields.email')}</th>
                    <th style={{width:'170px'}}>{t('userManagement.fields.profile')}</th>
                    {/* <th style={{width:'130px'}}>{t('userManagement.fields.status')}</th> */}
                    <th style={{width:'140px'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted small py-3">{t('userManagement.noUsers')}</td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td className="text-break" style={{maxWidth:'300px'}}>{u.email}</td>
                      <td>
                        <select className="form-select form-select-sm" value={u.id_perfil} onChange={e=>updateUser(u.id,{ id_perfil: Number(e.target.value) })}>
                          {perfiles.map(p=> <option key={p.id} value={p.id}>{p.descripcion || t('accessManagement.profile', { id: p.id })}</option>)}
                        </select>
                      </td>
                      {/* <td>
                        <select className="form-select form-select-sm" value={u.estado} onChange={e=>updateUser(u.id,{ estado: e.target.value })}>
                          <option value="A">Activo</option>
                          <option value="I">Inactivo</option>
                        </select>
                      </td> */}
                      <td className="text-end">
                        <div className="d-flex flex-column gap-1 align-items-end">
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-secondary" title={t('userManagement.actions.resetPassword')} onClick={()=>openReset(u)}>
                              <i className="bi bi-key" /> {t('userManagement.modal.reset')}
                            </button>
                            {u.estado === 'A' ? (
                              <button className="btn btn-outline-danger" title={t('userManagement.actions.deactivate')} onClick={()=>requestDeactivate(u)} disabled={actionLoading}>
                                <i className="bi bi-person-dash" />
                              </button>
                            ) : (
                              <button className="btn btn-outline-success" title={t('userManagement.actions.activate')} onClick={()=>doReactivate(u)} disabled={actionLoading}>
                                <i className="bi bi-person-check" />
                              </button>
                            )}
                          </div>
                          {confirmRow === u.id && (
                            <div className="bg-light border rounded p-2 small" style={{maxWidth:'220px'}}>
                              <div className="fw-semibold mb-1">{t('userManagement.confirmDeactivation', { defaultValue: 'Confirmar desactivación' })}</div>
                              <div className="d-flex gap-2">
                                <button className="btn btn-danger btn-sm" onClick={()=>doDeactivate(u)} disabled={actionLoading}>
                                  {actionLoading ? '...' : t('common.yes')}
                                </button>
                                <button className="btn btn-outline-secondary btn-sm" onClick={cancelDeactivate} disabled={actionLoading}>{t('common.no')}</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="btn-group btn-group-sm" role="group">
              <button className="btn btn-outline-secondary" disabled={page===1 || loading} onClick={()=>setPage(p=>p-1)}>«</button>
              <button className="btn btn-outline-secondary" disabled>{page}</button>
              <button className="btn btn-outline-secondary" disabled={page===totalPages || loading} onClick={()=>setPage(p=>p+1)}>»</button>
            </div>
            <div className="small text-muted">{t('userManagement.showingRecords', { defaultValue: 'Mostrando {{count}} de {{total}} registros', count: users.length, total: total })}</div>
          </div>
        </div>
      </div>

      {/* Modal Reset Password (controlado por estado) */}
      {resetTarget && (
        <div style={{position:'fixed', inset:0, zIndex:1055}}>
          {/* Backdrop */}
          <div className="fade show" style={{position:'absolute', inset:0, background:'rgba(0,0,0,.5)'}} onClick={closeReset} />
          <div className="modal fade show" style={{display:'block', position:'relative', zIndex:1060}} tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header py-2">
                  <h5 className="modal-title fs-6 mb-0">{t('userManagement.modal.resetPasswordTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeReset} disabled={resetLoading} />
                </div>
                <form onSubmit={doReset}>
                  <div className="modal-body">
                    <p className="small mb-2">{t('userManagement.user', { defaultValue: 'Usuario' })}: <strong>{resetTarget.email}</strong></p>
                    {!resetResult && (
                      <>
                        <label className="form-label small">{t('userManagement.newPasswordOptional', { defaultValue: 'Nueva contraseña (opcional, dejar vacío para generar aleatoria)' })}</label>
                        <input className="form-control form-control-sm" type="text" value={customPassword} onChange={e=>setCustomPassword(e.target.value)} disabled={resetLoading} placeholder={t('userManagement.generate', { defaultValue: '(generar)' })} />
                      </>
                    )}
                    {resetResult && (
                      <div className="alert alert-success small mb-0">
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-semibold">{t('userManagement.modal.newPassword')}</div>
                            <code className="user-select-all">{resetResult.new_password}</code>
                          </div>
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={()=>copyToClipboard(resetResult.new_password)}>
                            {t('userManagement.modal.copyPassword')}
                          </button>
                        </div>
                        <div className="mt-2 small text-muted">{t('userManagement.saveAndDeliverPassword', { defaultValue: 'Guárdala y entrégala al usuario. No volverá a mostrarse.' })}</div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer py-2">
                    {!resetResult ? (
                      <>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeReset} disabled={resetLoading}>{t('common.cancel')}</button>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={resetLoading}>{resetLoading ? t('userManagement.modal.resetting') : t('userManagement.modal.reset')}</button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-success btn-sm" onClick={closeReset}>{t('common.close')}</button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
